import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, from, map, of, shareReplay, switchMap, tap, throwError } from 'rxjs';

import { GET_SHOW_DIVISIONS, GET_COMBINATION_USAGE } from '../../../core/constants/api-endpoints';
import { ShowCatalogCacheStore } from '../../../core/services/show-catalog-cache.store';
import { ValidationApiService } from '../../../core/services/validation-api.service';
import { GetShowsDivisionsResponse, CombinationUsageResponse } from '../../../models/sa/show-divisions';
import { normalizeKeysDeep } from '../../utils/case-normalizer';

/** How long a persisted entry-less catalogue is trusted before refetching. */
const CATALOG_TTL_MS = 12 * 60 * 60 * 1000;

/**
 * The show's division/class catalogue — the one place it is fetched.
 *
 * `Division/GetShowDivisions` returns every result set the retired
 * `Master/GetShowsDivisions` and `Division/DivisionClassValidation` did
 * (`divisions`, `classes`, `fees`, `lineItemsFormFields`, `subDivisions`,
 * `fieldSettings`) plus `passes`.
 *
 * Entry-keyed calls go through {@link ValidationApiService}. The entry-less
 * form (~400 KB, same for everyone on the show) is persisted in IndexedDB and
 * refetched only on login, after {@link CATALOG_TTL_MS}, or on {@link invalidate}.
 */
@Injectable({ providedIn: 'root' })
export class ShowCatalogService {
  private readonly http = inject(HttpClient);
  private readonly validationApi = inject(ValidationApiService);
  private readonly store = inject(ShowCatalogCacheStore);

  /** In-flight/settled entry-less catalogue per show, so concurrent callers share one read. */
  private readonly cache = new Map<string, Observable<GetShowsDivisionsResponse>>();

  /** County-scoped entry-less catalogues (hdgs), keyed `show::countyId`. Memory only. */
  private readonly countyCache = new Map<string, Observable<GetShowsDivisionsResponse>>();

  /**
   * @param showId Show abbreviation the endpoints are scoped by
   * @param entryUuid Entry being built or edited; omit (or pass `'new'`) when
   *   the caller has no entry, which takes the fallback path described above
   * @param countyId County of the chosen exhibitor. Only sent when there is no
   *   entry — with an entry the API reads the county off the entry's exhibitor.
   */
  load(showId: string, entryUuid?: string, countyId?: number | null): Observable<GetShowsDivisionsResponse> {
    const show = showId || 'sa';
    const uuid = entryUuid && entryUuid !== 'new' ? entryUuid : '';

    // With an entry to key on, go through ValidationApiService so this shares
    // one HTTP call with the quantity rules — the wizard asks for both in the
    // same forkJoin, and it is the same URL.
    if (uuid) {
      return this.validationApi.getValidationData(uuid, show).pipe(map((response) => this.unwrap(response)));
    }

    if (countyId) {
      return this.loadForCounty(show, countyId);
    }

    let catalogue = this.cache.get(show);
    if (!catalogue) {
      catalogue = from(this.store.get<GetShowsDivisionsResponse>(show)).pipe(
        switchMap((hit) =>
          hit && Date.now() - hit.cachedAt < CATALOG_TTL_MS
            ? of(hit.data)
            : this.fetchWithoutEntry(show).pipe(tap((data) => void this.store.put(show, data)))
        ),
        shareReplay({ bufferSize: 1, refCount: false }),
        catchError((error) => {
          // Don't replay a failure to every later caller.
          this.cache.delete(show);
          return throwError(() => error);
        })
      );
      this.cache.set(show, catalogue);
    }

    return catalogue;
  }

  private loadForCounty(show: string, countyId: number): Observable<GetShowsDivisionsResponse> {
    const key = `${show}::${countyId}`;
    let catalogue = this.countyCache.get(key);
    if (!catalogue) {
      catalogue = this.http.post<unknown>(GET_SHOW_DIVISIONS(show), { countyId }).pipe(
        map((response) => this.unwrap(response)),
        shareReplay({ bufferSize: 1, refCount: false }),
        catchError((error) => {
          this.countyCache.delete(key);
          return throwError(() => error);
        })
      );
      this.countyCache.set(key, catalogue);
    }
    return catalogue;
  }

  /**
   * classUuid / passUuid (lowercased) → display name, from the catalogue's flat
   * `classes` and `passes` sets. Resolves empty on failure: names are cosmetic.
   */
  itemNames(showId: string): Observable<Map<string, string>> {
    return this.load(showId).pipe(
      map(toItemNameMap),
      catchError(() => of(new Map<string, string>()))
    );
  }

  /**
   * Drop every cached entry-less catalogue (memory and IndexedDB) — on login
   * and logout. Entry-keyed responses are invalidated through
   * {@link ValidationApiService.clearCache}.
   */
  clearCache(): void {
    this.cache.clear();
    this.countyCache.clear();
    void this.store.clear();
  }

  /** Force the next read of one show's catalogue to refetch — after an admin edit. */
  invalidate(showId = 'sa'): void {
    this.cache.delete(showId);
    for (const key of Array.from(this.countyCache.keys())) {
      if (key.startsWith(`${showId}::`)) {
        this.countyCache.delete(key);
      }
    }
    void this.store.delete(showId);
  }

  /**
   * How many animals of each `maxPerCombination` combination (e.g. Delaine - Ram)
   * the exhibitor already has saved for the show. `entryUuid` is the entry being
   * edited — its own answers are on screen, so the API leaves them out.
   */
  getCombinationUsage(
    showId: string,
    classUuid: string,
    familyMemberUuid: string,
    entryUuid?: string | null
  ): Observable<CombinationUsageResponse> {
    return this.http
      .post<{ data: CombinationUsageResponse }>(GET_COMBINATION_USAGE(showId), {
        classUuid,
        familyMemberUuid,
        entryUuid: entryUuid || null
      })
      .pipe(map((response) => normalizeKeysDeep<CombinationUsageResponse>(response.data)));
  }

  /**
   * The catalogue for callers with no entry — the cart's class-name lookup and
   * the post-login seed.
   */
  private fetchWithoutEntry(showId: string): Observable<GetShowsDivisionsResponse> {
    // An empty body, not none: the action casts it to a JsonElement.
    return this.http.post<unknown>(GET_SHOW_DIVISIONS(showId), {}).pipe(map((response) => this.unwrap(response)));
  }

  /**
   * Take the rows out of the `{ data, success, message }` envelope.
   *
   * A rejected envelope is raised rather than returned as empty rows: the
   * services this replaced unwrapped through an `assertSuccess` that threw, and
   * the wizard relies on that to warn "Could not load divisions" instead of
   * silently showing an empty Classes step.
   */
  private unwrap(response: unknown): GetShowsDivisionsResponse {
    const body = response as {
      data?: GetShowsDivisionsResponse;
      success?: boolean;
      message?: string;
      errorDetails?: string;
    } | null;

    if (body && typeof body === 'object' && body.success === false) {
      throw new Error(body.message || body.errorDetails || 'Request failed.');
    }

    return (body?.data ?? body ?? {}) as GetShowsDivisionsResponse;
  }
}

function toItemNameMap(data: GetShowsDivisionsResponse): Map<string, string> {
  const names = new Map<string, string>();
  const add = (rows: unknown, uuidKey: string, nameKey: string): void => {
    normalizeKeysDeep<Record<string, unknown>[]>(Array.isArray(rows) ? rows : []).forEach((row) => {
      const uuid = row[uuidKey];
      const name = row['description'] || row[nameKey];
      if (typeof uuid === 'string' && typeof name === 'string' && name) {
        names.set(uuid.toLowerCase(), name);
      }
    });
  };

  add(data.Classes ?? data.classes, 'classUuid', 'class');
  add(data.Passes ?? data.passes, 'passUuid', 'pass');
  return names;
}
